import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Play, RotateCcw, Settings as SettingsIcon, Accessibility, Info, BookOpen, Users } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import { hasSavedGame } from '../store/persistence';
import { loadStats } from '../store/persistence';
import { IMAGES } from '../assets';
import audioManager from '../audio/audioManager';

export default function Home() {
  const navigate = useNavigate();
  const { settings, update } = useSettings();
  const [saved] = useState(() => hasSavedGame());
  const stats = loadStats();

  const go = (path, state) => {
    audioManager.unlock();
    navigate(path, { state });
  };

  const toggleAccessible = () => {
    audioManager.unlock();
    update({ accessibleMode: !settings.accessibleMode });
  };

  return (
    <div
      className="relative min-h-[100dvh] w-full overflow-hidden flex flex-col items-center justify-center px-6 py-10"
      data-testid="home-screen"
    >
      <img
        src={IMAGES.homeBg}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover"
        draggable={false}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black/80" />

      <motion.div
        className="relative z-10 flex flex-col items-center w-full max-w-sm"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <img
          src={IMAGES.logo}
          alt="Logo Scopa Napoletana"
          className="w-28 h-28 object-cover rounded-full ring-2 ring-[#D4AF37]/60 drop-shadow-[0_6px_16px_rgba(0,0,0,0.6)]"
          draggable={false}
        />
        <h1 className="mt-3 font-serif text-5xl sm:text-6xl font-bold text-[#FDFBF7] tracking-tight text-center drop-shadow-lg">
          Scopa
        </h1>
        <p className="mt-1 font-sans text-sm tracking-[0.28em] uppercase text-[#D4AF37]">
          Carte Napoletane
        </p>

        <div className="mt-10 w-full flex flex-col gap-3.5">
          <button
            type="button"
            onClick={() => go('/game', { mode: 'new' })}
            data-testid="new-game-btn"
            className="group flex items-center justify-center gap-2 w-full rounded-full bg-[#D4AF37] text-[#14452F] font-sans font-bold text-base py-4 shadow-lg shadow-black/30 transition-colors hover:bg-[#F2D675] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#F2D675]/60 active:translate-y-px"
          >
            <Play className="w-5 h-5" />
            Nuova partita
          </button>

          {saved && (
            <button
              type="button"
              onClick={() => go('/game', { mode: 'continue' })}
              data-testid="continue-game-btn"
              className="flex items-center justify-center gap-2 w-full rounded-full bg-[#2C1A14]/90 text-[#FDFBF7] border border-[#D4AF37]/40 font-sans font-semibold text-base py-3.5 transition-colors hover:bg-[#3D261E] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#D4AF37]/50 active:translate-y-px"
            >
              <RotateCcw className="w-5 h-5" />
              Continua partita
            </button>
          )}

          <button
            type="button"
            onClick={() => go('/online')}
            data-testid="online-btn"
            className="flex items-center justify-center gap-2 w-full rounded-full bg-[#14452F] text-[#F2D675] border-2 border-[#D4AF37] font-sans font-bold text-base py-4 shadow-lg shadow-black/30 transition-colors hover:bg-[#1B5A3C] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#D4AF37]/50 active:translate-y-px"
          >
            <Users className="w-5 h-5" />
            Gioca online
          </button>

          <button
            type="button"
            onClick={() => go('/settings')}
            data-testid="settings-btn"
            className="mt-3 flex items-center justify-center gap-2 w-full rounded-full bg-[#2C1A14]/55 text-[#FDFBF7]/80 border border-[#D4AF37]/20 font-sans font-medium text-sm py-2.5 transition-colors hover:bg-[#3D261E]/70 hover:text-[#FDFBF7] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#D4AF37]/40 active:translate-y-px"
          >
            <SettingsIcon className="w-4 h-4" />
            Impostazioni
          </button>

          <button
            type="button"
            onClick={() => go('/come-si-gioca')}
            data-testid="how-to-play-btn"
            className="flex items-center justify-center gap-2 w-full rounded-full bg-[#2C1A14]/55 text-[#FDFBF7]/80 border border-[#D4AF37]/20 font-sans font-medium text-sm py-2.5 transition-colors hover:bg-[#3D261E]/70 hover:text-[#FDFBF7] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#D4AF37]/40 active:translate-y-px"
          >
            <BookOpen className="w-4 h-4" />
            Come si gioca
          </button>

          <button
            type="button"
            onClick={toggleAccessible}
            data-testid="accessibility-toggle-btn"
            aria-pressed={settings.accessibleMode}
            aria-label={`Modalità accessibile ${
              settings.accessibleMode ? 'attiva' : 'disattiva'
            }. Attiva per una doppia toccata.`}
            className={`flex items-center justify-center gap-2 w-full rounded-full font-sans font-medium text-sm py-2.5 transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#F2D675]/60 active:translate-y-px ${
              settings.accessibleMode
                ? 'bg-[#14452F] text-[#D4AF37] border-2 border-[#D4AF37]'
                : 'bg-[#2C1A14]/55 text-[#FDFBF7]/80 border border-[#D4AF37]/20 hover:bg-[#3D261E]/70 hover:text-[#FDFBF7]'
            }`}
          >
            <Accessibility className="w-4 h-4" />
            Accessibilità: {settings.accessibleMode ? 'ON' : 'OFF'}
          </button>
        </div>

        {settings.accessibleMode && (
          <p
            className="mt-4 flex items-start gap-2 text-xs text-[#E0E0D1] bg-black/40 rounded-xl px-3 py-2 max-w-xs"
            data-testid="accessible-hint"
          >
            <Info className="w-4 h-4 shrink-0 mt-0.5 text-[#D4AF37]" />
            Modalità accessibile attiva: telecronaca e navigazione VoiceOver
            abilitate. Puoi disattivarla dalle Impostazioni.
          </p>
        )}

        <p className="mt-6 text-xs text-[#E0E0D1]/70 font-sans">
          Partite: {stats.played} · Vinte: {stats.won} · Perse: {stats.lost}
        </p>
      </motion.div>
    </div>
  );
}
